/**
 * Excel (.xlsx) export for the task list.
 *
 * An .xlsx file is a ZIP of SpreadsheetML parts. This module writes those parts
 * itself (pure string builders, testable without a browser) and zips them with
 * JSZip, which already ships in the bundle as a dependency of `docx`.
 *
 * Dates are written as real Excel date serials (sortable/filterable in Excel),
 * strings as inline strings (never interpreted as formulas), with a bold frozen
 * header row and an auto-filter.
 *
 * @module services/xlsxExport
 */

import JSZip from 'jszip';
import { datePlaceholder } from '../utils/date.js';

// Excel number format codes for the app-wide display format. The English placeholder
// spells exactly that format, so it stays defined in src/utils/date.js only.
const EXCEL_DATE_FORMAT = datePlaceholder('en');
const EXCEL_DATE_TIME_FORMAT = `${EXCEL_DATE_FORMAT} hh:mm`;

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Excel's hard limit for a single cell's text.
const MAX_CELL_CHARS = 32767;
const MAX_SHEET_NAME_CHARS = 31;
const MS_PER_DAY = 86400000;
// Excel's day 0 in the 1900 date system (accounts for the 1900 leap-year bug).
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

// Style indexes into cellXfs in buildStylesXml().
const STYLE_HEADER = 1;
const STYLE_DATE = 2;
const STYLE_DATETIME = 3;
// Index 4 (wrapped top-aligned text) is reserved in cellXfs; rows now stay single-line.
const STYLE_TEXT = 5;
const STYLE_BADGE_BASE = 6;

// Status / priority "pill" colours: [fill, font] as ARGB. Soft fill + strong text,
// matching the badge families used in the app.
const BADGES = [
    ['status:backlog', 'FFE5E7EB', 'FF4B5563'],
    ['status:todo', 'FFDBEAFE', 'FF1D4ED8'],
    ['status:progress', 'FFFEF3C7', 'FFB45309'],
    ['status:review', 'FFEDE9FE', 'FF6D28D9'],
    ['status:done', 'FFD1FAE5', 'FF047857'],
    ['priority:high', 'FFFEE2E2', 'FFB91C1C'],
    ['priority:medium', 'FFFEF3C7', 'FFB45309'],
    ['priority:low', 'FFD1FAE5', 'FF047857']
];
const BADGE_STYLE = new Map(BADGES.map(([key], i) => [key, STYLE_BADGE_BASE + i]));
const BODY_ROW_HEIGHT = 22;
const HEADER_ROW_HEIGHT = 26;

/**
 * Column definitions for the task export, in sheet order.
 * `labelKey` is resolved through the injected t(); `width` is in Excel character units.
 */
export const TASK_EXPORT_COLUMNS = [
    { key: 'id', labelKey: 'tasks.export.column.id', width: 8 },
    { key: 'title', labelKey: 'tasks.table.task', width: 48 },
    { key: 'status', labelKey: 'tasks.table.status', width: 14 },
    { key: 'priority', labelKey: 'tasks.table.priority', width: 12 },
    { key: 'project', labelKey: 'tasks.table.project', width: 28 },
    { key: 'tags', labelKey: 'tasks.table.tags', width: 28 },
    { key: 'startDate', labelKey: 'tasks.table.startDate', width: 13 },
    { key: 'endDate', labelKey: 'tasks.table.endDate', width: 13 },
    { key: 'createdAt', labelKey: 'tasks.export.column.created', width: 17 },
    { key: 'updatedAt', labelKey: 'tasks.table.updated', width: 17 },
    { key: 'description', labelKey: 'tasks.export.column.description', width: 60 }
];

/**
 * Escape text for XML and drop characters XML 1.0 cannot carry.
 * @param {*} value
 * @returns {string}
 */
export function escapeXml(value) {
    return String(value ?? '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Zero-based column index → Excel column letters (0 → A, 25 → Z, 26 → AA).
 * @param {number} index
 * @returns {string}
 */
export function columnLetter(index) {
    let n = index + 1;
    let out = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}

/**
 * ISO calendar date (YYYY-MM-DD, optionally with a time part) → Excel date serial.
 * Only the calendar date is used, so no timezone shift can move the day.
 * @param {string} value
 * @returns {number|null}
 */
export function isoDateToExcelSerial(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    if (!match) return null;
    const [, y, m, d] = match.map(Number);
    const utc = Date.UTC(y, m - 1, d);
    const check = new Date(utc);
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
    return (utc - EXCEL_EPOCH_UTC) / MS_PER_DAY;
}

/**
 * Timestamp → Excel date-time serial using the viewer's local wall-clock time,
 * matching what the app shows in the Updated column.
 * @param {string|number|Date} value
 * @returns {number|null}
 */
export function timestampToExcelSerial(value) {
    if (value === null || value === undefined || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    const time = d.getTime();
    if (!Number.isFinite(time) || time === 0) return null;
    const localAsUtc = Date.UTC(
        d.getFullYear(), d.getMonth(), d.getDate(),
        d.getHours(), d.getMinutes(), d.getSeconds()
    );
    return (localAsUtc - EXCEL_EPOCH_UTC) / MS_PER_DAY;
}

const HTML_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/**
 * Rich-editor HTML → plain text with line breaks preserved between blocks.
 * @param {string} html
 * @returns {string}
 */
export function htmlToPlainText(html) {
    if (!html) return '';
    return String(html)
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<\s*li[^>]*>/gi, '• ')
        .replace(/<\/\s*(p|div|li|h[1-6]|tr|blockquote|pre)\s*>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, code) => {
            if (code[0] === '#') {
                const num = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
                return Number.isFinite(num) && num > 0 && num <= 0x10FFFF ? String.fromCodePoint(num) : '';
            }
            return HTML_ENTITIES[code.toLowerCase()] ?? whole;
        })
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Make a string a valid Excel sheet name (≤31 chars, none of []:*?/\).
 * @param {string} name
 * @returns {string}
 */
export function sanitizeSheetName(name) {
    const cleaned = String(name || '').replace(/[[\]:*?/\\]/g, ' ').replace(/^'+|'+$/g, '').trim();
    return (cleaned || 'Sheet1').slice(0, MAX_SHEET_NAME_CHARS);
}

/**
 * Map tasks to export rows. Each cell is `{ type: 'string'|'number'|'date'|'datetime', value }`
 * or null for an empty cell.
 *
 * @param {Array<Object>} tasks - Tasks in the order they should appear
 * @param {Object} helpers
 * @param {Array<Object>} helpers.projects
 * @param {(status: string) => string} helpers.getStatusLabel
 * @param {(priority: string) => string} helpers.getPriorityLabel
 * @returns {Array<Array<Object|null>>}
 */
export function buildTaskExportRows(tasks, helpers) {
    const { projects = [], getStatusLabel, getPriorityLabel } = helpers;
    const projectNames = new Map(projects.map((p) => [p.id, p.name]));
    const str = (value, style) => (value ? { type: 'string', value: String(value), style } : null);
    const badge = (kind, raw, label) => str(label, BADGE_STYLE.get(`${kind}:${String(raw || '').toLowerCase()}`));
    const date = (value) => {
        const serial = isoDateToExcelSerial(value);
        return serial === null ? null : { type: 'date', value: serial };
    };
    const datetime = (value) => {
        const serial = timestampToExcelSerial(value);
        return serial === null ? null : { type: 'datetime', value: serial };
    };

    return tasks.map((task) => {
        const cells = {
            id: Number.isFinite(Number(task.id)) && task.id !== '' && task.id !== null
                ? { type: 'number', value: Number(task.id) }
                : str(task.id),
            title: str(task.title, STYLE_TEXT),
            status: badge('status', task.status, task.status ? getStatusLabel(task.status) : ''),
            priority: badge('priority', task.priority, task.priority ? getPriorityLabel(task.priority) : ''),
            project: str(projectNames.get(task.projectId) || '', STYLE_TEXT),
            tags: str(Array.isArray(task.tags) ? task.tags.join(', ') : '', STYLE_TEXT),
            startDate: date(task.startDate),
            endDate: date(task.endDate),
            createdAt: datetime(task.createdAt || task.createdDate),
            updatedAt: datetime(task.updatedAt || task.createdAt || task.createdDate),
            description: str(htmlToPlainText(task.description), STYLE_TEXT)
        };
        return TASK_EXPORT_COLUMNS.map((col) => cells[col.key]);
    });
}

function cellXml(cell, ref, isHeader) {
    if (!cell) return '';
    if (cell.type === 'number' || cell.type === 'date' || cell.type === 'datetime') {
        const style = cell.type === 'date' ? STYLE_DATE : cell.type === 'datetime' ? STYLE_DATETIME : 0;
        return `<c r="${ref}"${style ? ` s="${style}"` : ''}><v>${cell.value}</v></c>`;
    }
    const text = String(cell.value).slice(0, MAX_CELL_CHARS);
    const style = isHeader ? STYLE_HEADER : (cell.style || 0);
    return `<c r="${ref}" t="inlineStr"${style ? ` s="${style}"` : ''}><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
}

/**
 * Build the worksheet XML.
 * @param {Object} sheet
 * @param {string[]} sheet.headers
 * @param {Array<Array<Object|null>>} sheet.rows
 * @param {number[]} sheet.widths
 * @returns {string}
 */
export function buildSheetXml({ headers, rows, widths }) {
    const lastCol = columnLetter(headers.length - 1);
    const lastRow = rows.length + 1;
    const cols = widths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('');
    const headerRow = `<row r="1" ht="${HEADER_ROW_HEIGHT}" customHeight="1">${headers
        .map((h, i) => cellXml({ type: 'string', value: h }, `${columnLetter(i)}1`, true))
        .join('')}</row>`;
    const bodyRows = rows
        .map((row, r) => `<row r="${r + 2}" ht="${BODY_ROW_HEIGHT}" customHeight="1">${row
            .map((cell, c) => cellXml(cell, `${columnLetter(c)}${r + 2}`, false))
            .join('')}</row>`)
        .join('');

    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
        + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
        + `<dimension ref="A1:${lastCol}${lastRow}"/>`
        + '<sheetViews><sheetView workbookViewId="0">'
        + '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
        + '<selection pane="bottomLeft" activeCell="A2" sqref="A2"/>'
        + '</sheetView></sheetViews>'
        + '<sheetFormatPr defaultRowHeight="15"/>'
        + `<cols>${cols}</cols>`
        + `<sheetData>${headerRow}${bodyRows}</sheetData>`
        + `<autoFilter ref="A1:${lastCol}${lastRow}"/>`
        + '</worksheet>';
}

/**
 * Build the styles part: default, bold header, dd/mm/yyyy date, dd/mm/yyyy hh:mm date-time,
 * wrapped text. Formats match the app-wide display format.
 * @returns {string}
 */
export function buildStylesXml() {
    const fonts = [
        '<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>',
        '<font><b/><sz val="11"/><color rgb="FF111827"/><name val="Calibri"/><family val="2"/></font>',
        ...BADGES.map(([, , font]) => `<font><b/><sz val="10"/><color rgb="${font}"/><name val="Calibri"/><family val="2"/></font>`)
    ];
    const fills = [
        '<fill><patternFill patternType="none"/></fill>',
        '<fill><patternFill patternType="gray125"/></fill>',
        '<fill><patternFill patternType="solid"><fgColor rgb="FFE2E8F0"/><bgColor indexed="64"/></patternFill></fill>',
        ...BADGES.map(([, fill]) => `<fill><patternFill patternType="solid"><fgColor rgb="${fill}"/><bgColor indexed="64"/></patternFill></fill>`)
    ];
    const border = '<border><left/><right/><top/><bottom style="thin"><color rgb="FFE5E7EB"/></bottom><diagonal/></border>';
    const center = '<alignment horizontal="center" vertical="center"/>';
    const xfs = [
        '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>',
        `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center"/></xf>`,
        `<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1">${center}</xf>`,
        `<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1">${center}</xf>`,
        '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>',
        '<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>',
        ...BADGES.map((_, i) => `<xf numFmtId="0" fontId="${i + 2}" fillId="${i + 3}" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">${center}</xf>`)
    ];
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
        + '<numFmts count="2">'
        + `<numFmt numFmtId="164" formatCode="${EXCEL_DATE_FORMAT}"/>`
        + `<numFmt numFmtId="165" formatCode="${EXCEL_DATE_TIME_FORMAT}"/>`
        + '</numFmts>'
        + `<fonts count="${fonts.length}">${fonts.join('')}</fonts>`
        + `<fills count="${fills.length}">${fills.join('')}</fills>`
        + '<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border>' + border + '</borders>'
        + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
        + `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>`
        + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
        + '</styleSheet>';
}

/**
 * Build every part of a single-sheet workbook, keyed by its path inside the ZIP.
 * @param {Object} sheet
 * @param {string} sheet.sheetName
 * @param {string[]} sheet.headers
 * @param {Array<Array<Object|null>>} sheet.rows
 * @param {number[]} sheet.widths
 * @returns {Object<string, string>}
 */
export function buildXlsxParts({ sheetName, headers, rows, widths }) {
    const name = sanitizeSheetName(sheetName);
    const lastCol = columnLetter(headers.length - 1);
    const lastRow = rows.length + 1;
    const quotedName = `'${name.replace(/'/g, "''")}'`;

    return {
        '[Content_Types].xml': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
            + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
            + '<Default Extension="xml" ContentType="application/xml"/>'
            + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
            + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
            + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
            + '</Types>',
        '_rels/.rels': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
            + '</Relationships>',
        'xl/workbook.xml': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
            + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
            + `<sheets><sheet name="${escapeXml(name)}" sheetId="1" r:id="rId1"/></sheets>`
            + '<definedNames>'
            + `<definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">${escapeXml(quotedName)}!$A$1:$${lastCol}$${lastRow}</definedName>`
            + '</definedNames>'
            + '</workbook>',
        'xl/_rels/workbook.xml.rels': '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
            + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
            + '</Relationships>',
        'xl/styles.xml': buildStylesXml(),
        'xl/worksheets/sheet1.xml': buildSheetXml({ headers, rows, widths })
    };
}

/**
 * Zip workbook parts into .xlsx bytes.
 * @param {Object<string, string>} parts - Output of buildXlsxParts
 * @param {'blob'|'uint8array'} [type='blob']
 * @returns {Promise<Blob|Uint8Array>}
 */
export async function zipXlsxParts(parts, type = 'blob') {
    const zip = new JSZip();
    // [Content_Types].xml first, as Office writes it.
    Object.keys(parts).forEach((path) => zip.file(path, parts[path]));
    return zip.generateAsync({
        type,
        mimeType: XLSX_MIME,
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
    });
}

/**
 * Build a task-list workbook.
 * @param {Array<Object>} tasks - Tasks in display order
 * @param {Object} options
 * @param {(key: string) => string} options.t
 * @param {Array<Object>} options.projects
 * @param {(status: string) => string} options.getStatusLabel
 * @param {(priority: string) => string} options.getPriorityLabel
 * @param {'blob'|'uint8array'} [options.type='blob']
 * @returns {Promise<Blob|Uint8Array>}
 */
export async function buildTasksXlsx(tasks, { t, projects, getStatusLabel, getPriorityLabel, type = 'blob' }) {
    const parts = buildXlsxParts({
        sheetName: t('tasks.export.sheetName'),
        headers: TASK_EXPORT_COLUMNS.map((col) => t(col.labelKey)),
        widths: TASK_EXPORT_COLUMNS.map((col) => col.width),
        rows: buildTaskExportRows(tasks, { projects, getStatusLabel, getPriorityLabel })
    });
    return zipXlsxParts(parts, type);
}
